/**
 * usePermissions composable
 *
 * Centralised permission logic for the admin hierarchy:
 *   SuperAdmin > Organization > Location > Activity > ActivityWorkgroup
 *     > ActivityInstance > ActivityInstanceWorkgroup
 *
 * "Scoped" admins (Activity, ActivityWorkgroup, ActivityInstance,
 * ActivityInstanceWorkgroup) are only admins over *specific* objects whose IDs
 * are listed in the permission payload returned by  GET /auth/permissions.
 *
 * The composable reads two Vuex sources:
 *   • stSessionData          – legacy flags (isSuperAdmin, organizationadmin …)
 *   • stFullPermissionData   – the complete /auth/permissions response
 *
 * Expected shape of stFullPermissionData (from API):
 * {
 *   admin_level: "superadmin" | "organization" | "location" | "activity"
 *                | "activity_workgroup" | "activity_instance"
 *                | "activity_instance_workgroup" | null,
 *   admin_activity_ids:              [Number],  // activities user admins
 *   admin_workgroup_ids:             [Number],  // activity-workgroups user admins
 *   admin_instance_ids:              [Number],  // activity-instances user admins
 *   admin_instance_workgroup_ids:    [Number],  // activity-instance-workgroups user admins
 *   admin_workgroup_skill_ids:       [Number],  // skill IDs linked to user's admin workgroups
 *   admin_workgroup_derived_instance_workgroup_ids: [Number], // instance-workgroup IDs derived from user's admin workgroups
 *   cardslist: { ... }                          // existing card-level visibility
 * }
 */

import { computed } from "vue";
import { useStore } from "vuex";

/* ------------------------------------------------------------------ */
/*  Numeric hierarchy – higher number = more privilege                 */
/* ------------------------------------------------------------------ */
const LEVEL_RANK = {
    activity_instance_workgroup: 1,
    activity_instance: 2,
    activity_workgroup: 3,
    activity: 4,
    location: 5,
    organization: 6,
    superadmin: 7,
};

function rankOf(level) {
    return LEVEL_RANK[level] || 0;
}

/* ------------------------------------------------------------------ */
/*  Composable                                                        */
/* ------------------------------------------------------------------ */
export function usePermissions() {
    const store = useStore();

    /* ---------- raw sources ---------------------------------------- */
    const sessionData = computed(() => store.getters.stSessionData || {});
    const permData = computed(() => store.getters.stFullPermissionData || {});

    /* ---------- resolved admin level ------------------------------- */
    const adminLevel = computed(() => {
        // Prefer explicit API field
        if (permData.value.admin_level) return permData.value.admin_level;

        // Fallback to legacy session flags (best-effort mapping)
        const s = sessionData.value;
        if (s.isSuperAdmin || s.is_super_admin) return "superadmin";
        if (s.organizationadmin) return "organization";
        if (s.locationadmin) return "location";
        // NOTE: isAdminInLocation is intentionally NOT mapped to "location"
        // because it is true for ANY admin role (workgroup, instance, etc.)
        // and would incorrectly elevate scoped admins to full location access.
        return null;
    });

    const adminRank = computed(() => rankOf(adminLevel.value));

    /* ---------- boolean helpers (convenience) ---------------------- */
    const isSuperAdmin = computed(() => adminLevel.value === "superadmin");
    const isOrganizationAdmin = computed(() => adminRank.value >= LEVEL_RANK.organization);
    const isLocationAdmin = computed(() => adminRank.value >= LEVEL_RANK.location);
    const isActivityAdmin = computed(() => adminRank.value >= LEVEL_RANK.activity);
    const isActivityWorkgroupAdmin = computed(() => adminRank.value >= LEVEL_RANK.activity_workgroup);
    const isActivityInstanceAdmin = computed(() => adminRank.value >= LEVEL_RANK.activity_instance);
    const isActivityInstanceWorkgroupAdmin = computed(() => adminRank.value >= LEVEL_RANK.activity_instance_workgroup);

    /* "exactly" helpers – true only at that specific level */
    const isExactlyActivityAdmin = computed(() => adminLevel.value === "activity");
    const isExactlyActivityWorkgroupAdmin = computed(() => adminLevel.value === "activity_workgroup");
    const isExactlyActivityInstanceAdmin = computed(() => adminLevel.value === "activity_instance");
    const isExactlyActivityInstanceWorkgroupAdmin = computed(() => adminLevel.value === "activity_instance_workgroup");

    /* ---------- admin_permissions object ----------------------------- */
    const adminPermissions = computed(() => permData.value.admin_permissions || {});

    /* ---------- scoped ID lists ------------------------------------ */
    // API returns activity_ids / workgroup_ids; fall back to admin_ prefix for
    // backward compatibility with older payloads.
    const adminActivityIds = computed(
        () => permData.value.activity_ids || permData.value.admin_activity_ids || []
    );
    const adminWorkgroupIds = computed(
        () => permData.value.workgroup_ids || permData.value.admin_workgroup_ids || []
    );
    const adminInstanceIds = computed(() => permData.value.admin_instance_ids || []);
    const adminInstanceWorkgroupIds = computed(() => permData.value.admin_instance_workgroup_ids || []);
    const adminWorkgroupSkillIds = computed(() => permData.value.admin_workgroup_skill_ids || []);
    const adminDerivedInstanceWorkgroupIds = computed(
        () => permData.value.admin_workgroup_derived_instance_workgroup_ids || []
    );

    /* ================================================================
     *  ADMIN GROUPS – who can assign which admin activities / workgroups
     * ================================================================ */

    /**
     * Filter the full activity list to only those the current user may
     * grant admin access to another user for.
     *
     * @param {Array} allActivities  – [{ id, name, … }]
     * @returns {Array} filtered list
     */
    function getFilteredAdminActivities(allActivities) {
        if (!allActivities) return [];
        // Location, Organization, SuperAdmin → all
        if (isLocationAdmin.value) return allActivities;

        // Activity admin → only their own admin activities
        if (isExactlyActivityAdmin.value) {
            const ids = new Set(adminActivityIds.value.map(Number));
            return allActivities.filter((a) => ids.has(Number(a.id)));
        }

        // Below Activity level → cannot assign Activity admin groups
        return [];
    }

    /**
     * Filter the full workgroup list to only those the current user may
     * grant admin access to another user for.
     *
     * @param {Array} allWorkgroups  – [{ id, name, … }]
     * @returns {Array} filtered list
     */
    function getFilteredAdminWorkgroups(allWorkgroups) {
        if (!allWorkgroups) return [];
        // Location, Organization, SuperAdmin → all
        if (isLocationAdmin.value) return allWorkgroups;

        // Activity admin → workgroups within their admin activities
        if (isExactlyActivityAdmin.value) {
            const ids = new Set(adminWorkgroupIds.value.map(Number));
            return allWorkgroups.filter((w) => ids.has(Number(w.id)));
        }

        // ActivityWorkgroup admin → only their own admin workgroups + derived instance workgroups
        if (isExactlyActivityWorkgroupAdmin.value) {
            const own = new Set(adminWorkgroupIds.value.map(Number));
            const derived = new Set(adminDerivedInstanceWorkgroupIds.value.map(Number));
            return allWorkgroups.filter(
                (w) => own.has(Number(w.id)) || derived.has(Number(w.id))
            );
        }

        // ActivityInstance admin → instance workgroups of their instances
        if (isExactlyActivityInstanceAdmin.value) {
            const ids = new Set(adminInstanceWorkgroupIds.value.map(Number));
            return allWorkgroups.filter((w) => ids.has(Number(w.id)));
        }

        // ActivityInstanceWorkgroup admin → only their own instance workgroup
        if (isExactlyActivityInstanceWorkgroupAdmin.value) {
            const ids = new Set(adminInstanceWorkgroupIds.value.map(Number));
            return allWorkgroups.filter((w) => ids.has(Number(w.id)));
        }

        return [];
    }

    /** Whether the user can modify admin groups at all */
    const canModifyAdminGroups = computed(() => adminRank.value >= LEVEL_RANK.activity_instance_workgroup);

    /* ================================================================
     *  SKILLS – who can edit a skill
     * ================================================================ */

    /**
     * @param {Number|null} skillId – the skill being edited (optional; used
     *   for workgroup-admin scoped check)
     * @returns {{ canEdit: Boolean, reason: String }}
     */
    function canModifySkill(skillId) {
        // Activity Instance / Activity Instance Workgroup admins → never
        if (
            isExactlyActivityInstanceAdmin.value ||
            isExactlyActivityInstanceWorkgroupAdmin.value
        ) {
            return {
                canEdit: false,
                reason: "Activity Instance and Activity Instance Workgroup admins cannot modify skills.",
            };
        }

        // Activity Workgroup admin → only their own workgroup's skill
        if (isExactlyActivityWorkgroupAdmin.value) {
            const ids = new Set(adminWorkgroupSkillIds.value.map(Number));
            if (skillId && !ids.has(Number(skillId))) {
                return {
                    canEdit: false,
                    reason: "You can only modify the skill associated with your admin workgroup.",
                };
            }
            return { canEdit: true, reason: "" };
        }

        // Activity, Location, Organization, SuperAdmin → full access
        if (isActivityAdmin.value) {
            return { canEdit: true, reason: "" };
        }

        // Fallback: if admin level is unresolved but user has workgroup skill IDs,
        // treat as workgroup admin (scoped skill check).
        if (adminWorkgroupSkillIds.value.length > 0) {
            const ids = new Set(adminWorkgroupSkillIds.value.map(Number));
            if (skillId && !ids.has(Number(skillId))) {
                return {
                    canEdit: false,
                    reason: "You can only modify the skill associated with your admin workgroup.",
                };
            }
            return { canEdit: true, reason: "" };
        }

        return { canEdit: false, reason: "You do not have permission to modify skills." };
    }

    /* ================================================================
     *  PASSWORD – who can reset whose password
     * ================================================================ */

    /**
     * @param {Object} targetUser – the user record being edited, expected to
     *   contain boolean flags: is_organization_admin, is_location_admin,
     *   is_activity_admin (+ optionally skill IDs the target user has).
     * @returns {{ canUpdate: Boolean, reason: String }}
     */
    function canModifyPassword(targetUser) {
        const t = targetUser || {};

        // Activity Instance / Activity Instance Workgroup admins → never
        if (
            isExactlyActivityInstanceAdmin.value ||
            isExactlyActivityInstanceWorkgroupAdmin.value
        ) {
            return { canUpdate: false, reason: "Your admin level does not allow password changes." };
        }

        // SuperAdmin → always
        if (isSuperAdmin.value) {
            return { canUpdate: true, reason: "" };
        }

        // Organization admin → always
        if (adminLevel.value === "organization") {
            return { canUpdate: true, reason: "" };
        }

        // Location admin → cannot change Org admin passwords
        if (adminLevel.value === "location") {
            if (t.is_organization_admin) {
                return { canUpdate: false, reason: "Cannot modify an Organization admin's password." };
            }
            return { canUpdate: true, reason: "" };
        }

        // Activity admin → cannot change Location/Org admin passwords
        if (isExactlyActivityAdmin.value) {
            if (t.is_organization_admin || t.is_location_admin) {
                return {
                    canUpdate: false,
                    reason: "Cannot modify a Location or Organization admin's password.",
                };
            }
            return { canUpdate: true, reason: "" };
        }

        // Activity Workgroup admin → only users with their skill who are not Activity/Location/Org admins
        if (isExactlyActivityWorkgroupAdmin.value) {
            if (t.is_organization_admin || t.is_location_admin || t.is_activity_admin) {
                return {
                    canUpdate: false,
                    reason: "Cannot modify this admin user's password.",
                };
            }
            // Check if the target user has the skill linked to the admin workgroup
            const targetSkillIds = (t.skill_ids || []).map(Number);
            const mySkillIds = new Set(adminWorkgroupSkillIds.value.map(Number));
            const hasOverlap = targetSkillIds.some((id) => mySkillIds.has(id));
            if (!hasOverlap) {
                return {
                    canUpdate: false,
                    reason: "You can only modify passwords for users who have your admin workgroup's skill.",
                };
            }
            return { canUpdate: true, reason: "" };
        }

        return { canUpdate: false, reason: "You do not have permission to change passwords." };
    }

    /* ================================================================
     *  USER PROFILE FIELDS – same rules as password
     * ================================================================ */

    /**
     * Determines whether the current admin can modify a target user's core
     * profile fields (First Name, Middle Name, Last Name, Email, Proxy Email,
     * Use Proxy Email).  Uses the same hierarchy rules as canModifyPassword.
     *
     * @param {Object} targetUser – same shape as canModifyPassword's param
     * @returns {{ canEdit: Boolean, reason: String }}
     */
    function canModifyUserProfile(targetUser) {
        const t = targetUser || {};

        // Activity Instance / Activity Instance Workgroup admins → never
        if (
            isExactlyActivityInstanceAdmin.value ||
            isExactlyActivityInstanceWorkgroupAdmin.value
        ) {
            return { canEdit: false, reason: "Your admin level does not allow editing user profile fields." };
        }

        // SuperAdmin → always
        if (isSuperAdmin.value) {
            return { canEdit: true, reason: "" };
        }

        // Organization admin → always
        if (adminLevel.value === "organization") {
            return { canEdit: true, reason: "" };
        }

        // Location admin → cannot edit Org admin profiles
        if (adminLevel.value === "location") {
            if (t.is_organization_admin) {
                return { canEdit: false, reason: "Cannot modify an Organization admin's profile." };
            }
            return { canEdit: true, reason: "" };
        }

        // Activity admin → cannot edit Location/Org admin profiles
        if (isExactlyActivityAdmin.value) {
            if (t.is_organization_admin || t.is_location_admin) {
                return {
                    canEdit: false,
                    reason: "Cannot modify a Location or Organization admin's profile.",
                };
            }
            return { canEdit: true, reason: "" };
        }

        // Activity Workgroup admin → only users with their skill who are not Activity/Location/Org admins
        if (isExactlyActivityWorkgroupAdmin.value) {
            if (t.is_organization_admin || t.is_location_admin || t.is_activity_admin) {
                return {
                    canEdit: false,
                    reason: "Cannot modify this admin user's profile.",
                };
            }
            const targetSkillIds = (t.skill_ids || []).map(Number);
            const mySkillIds = new Set(adminWorkgroupSkillIds.value.map(Number));
            const hasOverlap = targetSkillIds.some((id) => mySkillIds.has(id));
            if (!hasOverlap) {
                return {
                    canEdit: false,
                    reason: "You can only modify profiles for users who have your admin workgroup's skill.",
                };
            }
            return { canEdit: true, reason: "" };
        }

        return { canEdit: false, reason: "You do not have permission to edit user profiles." };
    }

    /* ================================================================
     *  ACTIVITIES – who can edit / delete an activity
     * ================================================================ */

    /**
     * @param {Number} activityId
     * @returns {Boolean} true if user may navigate to the edit page for this activity
     */
    function canEditActivity(activityId) {
        // Location, Organization, SuperAdmin → always
        if (isLocationAdmin.value) return true;

        // Activity admin → only their scoped activities
        if (isExactlyActivityAdmin.value) {
            return adminActivityIds.value.map(Number).includes(Number(activityId));
        }

        // Workgroup admin → can open the activity edit page (read-only for
        // activity-level fields) if the activity is in their scoped list.
        if (isExactlyActivityWorkgroupAdmin.value) {
            return adminActivityIds.value.map(Number).includes(Number(activityId));
        }

        // Fallback: if admin level is unresolved (API didn't return admin_level)
        // but the user has scoped activity IDs, allow navigation (read-only
        // access is enforced separately by isReadOnlyForActivity).
        if (adminActivityIds.value.length > 0) {
            return adminActivityIds.value.map(Number).includes(Number(activityId));
        }

        // Instance / InstanceWorkgroup admins → view-only on list page
        return false;
    }

    /**
     * @param {Number} activityId
     * @returns {Boolean} true if user may delete the activity
     */
    function canDeleteActivity(activityId) {
        // Location, Organization, SuperAdmin → always
        if (isLocationAdmin.value) return true;

        // Activity admin → only their scoped activities
        if (isExactlyActivityAdmin.value) {
            return adminActivityIds.value.map(Number).includes(Number(activityId));
        }

        // Workgroup admins and below cannot delete activities
        return false;
    }

    /**
     * @param {Number} workgroupId
     * @returns {Boolean} true if user may edit this workgroup within an activity
     */
    function canEditWorkgroup(workgroupId) {
        // Location, Organization, SuperAdmin → always
        if (isLocationAdmin.value) return true;

        // Activity admin → all workgroups within their activities
        if (isExactlyActivityAdmin.value) return true;

        // Workgroup admin → only their own scoped workgroups
        if (isExactlyActivityWorkgroupAdmin.value) {
            return adminWorkgroupIds.value.map(Number).includes(Number(workgroupId));
        }

        // Fallback: if admin level is unresolved (API didn't return admin_level)
        // but the user has scoped workgroup IDs, allow editing their own workgroups.
        if (adminWorkgroupIds.value.length > 0) {
            return adminWorkgroupIds.value.map(Number).includes(Number(workgroupId));
        }

        return false;
    }

    /**
     * Returns true when the user has *view* access to the activity edit page
     * but must NOT modify activity-level fields (details, recurrences, etc.).
     * Workgroup admins fall into this category – they may only touch their own
     * workgroups.
     *
     * @param {Number} activityId
     * @returns {Boolean}
     */
    function isReadOnlyForActivity(activityId) {
        // Location+ → never read-only
        if (isLocationAdmin.value) return false;

        // Activity admin with access → not read-only for that activity
        if (isExactlyActivityAdmin.value) {
            return !adminActivityIds.value.map(Number).includes(Number(activityId));
        }

        // Workgroup admin → always read-only for activity-level fields
        if (isExactlyActivityWorkgroupAdmin.value) return true;

        // Fallback: if admin level is unresolved but user has scoped workgroup
        // IDs, treat as read-only (same as workgroup admin behaviour).
        if (adminWorkgroupIds.value.length > 0) return true;

        // Anything else (instance admins, etc.) → read-only
        return true;
    }

    /* ================================================================
     *  SKILL PERSONS – who can modify persons assigned to a skill
     * ================================================================ */

    /**
     * @param {Number|null} skillId
     * @returns {{ canEdit: Boolean, reason: String }}
     */
    function canModifySkillPersons(skillId) {
        // Activity admin and above → full access
        if (isActivityAdmin.value) {
            return { canEdit: true, reason: "" };
        }

        // Workgroup admin → only skills linked to their admin workgroups
        if (isExactlyActivityWorkgroupAdmin.value) {
            const ids = new Set(adminWorkgroupSkillIds.value.map(Number));
            if (skillId && ids.has(Number(skillId))) {
                return { canEdit: true, reason: "" };
            }
            return {
                canEdit: false,
                reason: "You can only modify persons for skills in your admin workgroup.",
            };
        }

        // Fallback: if admin level is unresolved but user has workgroup skill IDs,
        // treat as workgroup admin (scoped skill check).
        if (adminWorkgroupSkillIds.value.length > 0) {
            const ids = new Set(adminWorkgroupSkillIds.value.map(Number));
            if (skillId && ids.has(Number(skillId))) {
                return { canEdit: true, reason: "" };
            }
            return {
                canEdit: false,
                reason: "You can only modify persons for skills in your admin workgroup.",
            };
        }

        // Instance / InstanceWorkgroup admin → view only
        return {
            canEdit: false,
            reason: "Instance admins cannot modify skill assignments.",
        };
    }

    /* ================================================================
     *  USER ACCESS BY SKILL – can admin manage this user's rules /
     *  preferences / admin groups based on skill overlap?
     * ================================================================ */

    /**
     * @param {Object} targetUser – { skill_ids: [Number] }
     * @returns {{ canAccess: Boolean, reason: String }}
     */
    function canAccessUserBySkill(targetUser) {
        const t = targetUser || {};

        // Location+ → always
        if (isLocationAdmin.value) return { canAccess: true, reason: "" };

        // Sub-location admins: check skill overlap
        const targetSkillIds = (t.skill_ids || []).map(Number);
        const mySkillIds = new Set(adminWorkgroupSkillIds.value.map(Number));

        if (mySkillIds.size > 0 && targetSkillIds.length > 0) {
            const hasOverlap = targetSkillIds.some((id) => mySkillIds.has(id));
            if (hasOverlap) return { canAccess: true, reason: "" };
        }

        // Activity admin without populated skill IDs → grant (activity-level
        // access is broader; the API may not always return skill IDs).
        if (isExactlyActivityAdmin.value) return { canAccess: true, reason: "" };

        // If we have scoped skill IDs but no overlap → deny
        if (mySkillIds.size > 0) {
            return {
                canAccess: false,
                reason: "This user does not have any skills used by your admin scope.",
            };
        }

        return {
            canAccess: false,
            reason: "You do not have permission to manage this user.",
        };
    }

    /* ================================================================
     *  USER DELETION
     * ================================================================ */

    /**
     * @param {Object} targetUser – { skill_ids, is_organization_admin,
     *   is_location_admin, is_activity_admin }
     * @returns {{ canDelete: Boolean, reason: String }}
     */
    function canDeleteUser(targetUser) {
        const t = targetUser || {};

        // Instance / Instance Workgroup admins → never
        if (
            isExactlyActivityInstanceAdmin.value ||
            isExactlyActivityInstanceWorkgroupAdmin.value
        ) {
            return { canDelete: false, reason: "Instance admins cannot delete users." };
        }

        // Location+ → always
        if (isLocationAdmin.value) return { canDelete: true, reason: "" };

        // Activity admin → can delete non-admin users; for admin users the
        // backend validates they only administer workgroups within the
        // activity admin's scope.
        if (isExactlyActivityAdmin.value) {
            if (t.is_organization_admin || t.is_location_admin) {
                return {
                    canDelete: false,
                    reason: "Cannot delete a Location or Organization admin.",
                };
            }
            return { canDelete: true, reason: "" };
        }

        // Workgroup admin → only if user exclusively has admin's skills
        if (isExactlyActivityWorkgroupAdmin.value || adminWorkgroupSkillIds.value.length > 0) {
            const targetSkillIds = (t.skill_ids || []).map(Number);
            const mySkillIds = new Set(adminWorkgroupSkillIds.value.map(Number));

            if (targetSkillIds.length === 0) {
                return {
                    canDelete: false,
                    reason: "Cannot determine user's skills for deletion check.",
                };
            }

            const allInScope = targetSkillIds.every((id) => mySkillIds.has(id));
            if (!allInScope) {
                return {
                    canDelete: false,
                    reason: "You can only delete users who exclusively have your admin workgroup's skill(s).",
                };
            }
            return { canDelete: true, reason: "" };
        }

        return { canDelete: false, reason: "You do not have permission to delete users." };
    }

    /* ================================================================
     *  SKILL DELETION
     * ================================================================ */

    /**
     * @param {Number} skillId
     * @returns {Boolean}
     */
    function canDeleteSkill(/* skillId */) {
        // Location+ → always
        if (isLocationAdmin.value) return true;

        // Activity admin → allowed (backend enforces workgroup-usage rule)
        if (isExactlyActivityAdmin.value) return true;

        // Fallback for unresolved admin level with activity IDs
        if (adminActivityIds.value.length > 0) return true;

        // Workgroup / Instance admins → cannot delete skills
        return false;
    }

    /* ================================================================
     *  ACTIVITY INSTANCE – who can edit a specific instance
     * ================================================================ */

    /**
     * @param {Number} instanceId
     * @returns {Boolean}
     */
    function canEditInstance(instanceId) {
        if (isLocationAdmin.value) return true;
        if (isExactlyActivityAdmin.value) return true;

        if (isExactlyActivityInstanceAdmin.value) {
            return adminInstanceIds.value.map(Number).includes(Number(instanceId));
        }

        // Fallback: check adminInstanceIds directly
        if (adminInstanceIds.value.length > 0) {
            return adminInstanceIds.value.map(Number).includes(Number(instanceId));
        }

        // Workgroup / IWG admins → view only at instance level
        return false;
    }

    /**
     * @param {Number} instanceId
     * @returns {Boolean}
     */
    function isReadOnlyForInstance(instanceId) {
        if (isLocationAdmin.value) return false;
        if (isExactlyActivityAdmin.value) return false;

        if (isExactlyActivityInstanceAdmin.value) {
            return !adminInstanceIds.value.map(Number).includes(Number(instanceId));
        }

        // Fallback: if user has scoped instance IDs, check membership
        if (adminInstanceIds.value.length > 0) {
            return !adminInstanceIds.value.map(Number).includes(Number(instanceId));
        }

        // Workgroup / IWG admins → read-only at instance level
        return true;
    }

    /* ================================================================
     *  INSTANCE WORKGROUP – who can edit a specific instance workgroup
     * ================================================================ */

    /**
     * @param {Number} iwgId
     * @returns {Boolean}
     */
    function canEditInstanceWorkgroup(iwgId) {
        if (isLocationAdmin.value) return true;
        if (isExactlyActivityAdmin.value) return true;
        if (isExactlyActivityInstanceAdmin.value) return true;

        // Workgroup admin → derived instance workgroups
        if (isExactlyActivityWorkgroupAdmin.value) {
            return adminDerivedInstanceWorkgroupIds.value.map(Number).includes(Number(iwgId));
        }

        // Instance Workgroup admin → only their own
        if (isExactlyActivityInstanceWorkgroupAdmin.value) {
            return adminInstanceWorkgroupIds.value.map(Number).includes(Number(iwgId));
        }

        // Fallback: check both ID arrays directly
        if (adminDerivedInstanceWorkgroupIds.value.length > 0) {
            if (adminDerivedInstanceWorkgroupIds.value.map(Number).includes(Number(iwgId))) {
                return true;
            }
        }
        if (adminInstanceWorkgroupIds.value.length > 0) {
            return adminInstanceWorkgroupIds.value.map(Number).includes(Number(iwgId));
        }

        return false;
    }

    /**
     * @param {Number} iwgId
     * @returns {Boolean}
     */
    function isReadOnlyForInstanceWorkgroup(iwgId) {
        return !canEditInstanceWorkgroup(iwgId);
    }

    /* ================================================================
     *  SETUP WIZARD – only Location/Organization/SuperAdmin
     * ================================================================ */
    const canAccessSetupWizard = computed(() => isLocationAdmin.value);

    /* ================================================================
     *  PUBLIC API
     * ================================================================ */
    return {
        // raw
        adminLevel,
        adminRank,
        sessionData,
        permData,

        // boolean "at-least" helpers
        isSuperAdmin,
        isOrganizationAdmin,
        isLocationAdmin,
        isActivityAdmin,
        isActivityWorkgroupAdmin,
        isActivityInstanceAdmin,
        isActivityInstanceWorkgroupAdmin,

        // boolean "exactly" helpers
        isExactlyActivityAdmin,
        isExactlyActivityWorkgroupAdmin,
        isExactlyActivityInstanceAdmin,
        isExactlyActivityInstanceWorkgroupAdmin,

        // scoped IDs
        adminActivityIds,
        adminWorkgroupIds,
        adminInstanceIds,
        adminInstanceWorkgroupIds,
        adminWorkgroupSkillIds,
        adminDerivedInstanceWorkgroupIds,

        // admin-group helpers
        canModifyAdminGroups,
        getFilteredAdminActivities,
        getFilteredAdminWorkgroups,

        // skill helpers
        canModifySkill,
        canModifySkillPersons,

        // activity / workgroup helpers
        canEditActivity,
        canDeleteActivity,
        canEditWorkgroup,
        isReadOnlyForActivity,

        // admin_permissions object
        adminPermissions,

        // password helpers
        canModifyPassword,

        // user profile helpers
        canModifyUserProfile,

        // user access / deletion
        canAccessUserBySkill,
        canDeleteUser,

        // skill deletion
        canDeleteSkill,

        // instance / instance-workgroup helpers
        canEditInstance,
        isReadOnlyForInstance,
        canEditInstanceWorkgroup,
        isReadOnlyForInstanceWorkgroup,

        // setup wizard
        canAccessSetupWizard,
    };
}
